import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  Icon,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Badge,
  Divider
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FiUser, FiSearch, FiRefreshCw, FiUserCheck, FiCreditCard, FiHash } from 'react-icons/fi';
import PartnerDataBox from '../components/partner-data-box';
import RadioButtons from '../components/radio-group';
import { Member } from '../models/member';
import { isMobile } from 'react-device-detect';
import { findMember } from '../services/member-service';
import ColorModeToggle from '../components/color-mode-toggle';

const PENDING = 'Pending';
const IN_PROGRESS = 'In Progress';
const COMPLETE = 'Complete';
const NOT_FOUND = 'Not Found';
const FOUND = 'Found';
const MULTIPLE_FOUND = 'Multiple Found';
const MULTIPLE_MEMBERS_MESSAGE = 'Multiple members match the search criteria';

const SEARCH_WARNING_MESSAGES: Record<string, string> = {
  [NOT_FOUND]:
    'No encontramos ningún socio con los datos brindados. Por favor, verificá la información e intentá nuevamente.',
  [MULTIPLE_FOUND]:
    'Con los datos brindados encontramos a más de un socio. Por favor, reintentá la búsqueda siendo un poco más preciso/a.'
};

function FirstLogin(): JSX.Element {
  const [searchType, setSearchType] = useState('1');
  const [partnerData, setPartnerData] = useState(null as unknown as Member);
  const [queryStatus, setQueryStatus] = useState(PENDING);
  const [queryResult, setQueryResult] = useState('');


  const bgGradient = useColorModeValue(
    'linear(135deg, gray.50 0%, green.50 100%)',
    'linear(135deg, gray.900 0%, gray.800 100%)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('green.700', 'green.300');
  const successCardBorder = useColorModeValue('green.100', 'gray.700');
  const successCardHeaderBg = useColorModeValue('green.500', 'green.600');

  const formik = useFormik({
    initialValues: {
      memberNumber: '',
      documentNumber: '',
      fullName: ''
    },
    validationSchema: Yup.object({
      memberNumber: Yup.number()
        .positive('El número de socio ingresado no es válido.')
        .integer('El número de socio ingresado no es válido.'),
      documentNumber: Yup.number()
        .positive('El número de documento ingresado no es válido.')
        .integer('El número de documento ingresado no es válido.'),
      fullName: Yup.string()
        .trim()
        .min(2, 'El nombre y/o apellido ingresado no es válido.')
    }),
    onSubmit: async (values) => {
      if (searchType === '3' && !values.fullName.trim()) {
        return;
      }
      if (searchType === '1' && values.memberNumber == null) {
        return;
      }
      if (searchType === '2' && values.documentNumber == null) {
        return;
      }

      setQueryStatus(IN_PROGRESS);
      const response = searchType === '3'
        ? await findMember({ fullName: values.fullName.trim() })
        : searchType === '1'
          ? await findMember({ memberNumber: Number(values.memberNumber) })
          : await findMember({ documentNumber: Number(values.documentNumber) });

      setQueryStatus(COMPLETE);
      switch (response.statusCode) {
        case 200:
          setQueryResult(FOUND);
          if (response.response !== null) {
            setPartnerData(response.response);
          }
          break;
        case 204:
          setQueryResult(NOT_FOUND);
          break;
        case 400:
          setQueryResult(
            response.message === MULTIPLE_MEMBERS_MESSAGE ? MULTIPLE_FOUND : NOT_FOUND
          );
          break;
        default:
          setQueryResult(NOT_FOUND);
          break;
      }
    }
  });

  const handleRadioButtonChange = (selectedValue: string): void => {
    setSearchType(selectedValue);
  };

  const retrySearch = (): void => {
    setQueryStatus(PENDING);
    setQueryResult('');
    formik.resetForm();
  };

  const SearchButtons = (): JSX.Element => {
    const isSearchDisabled = queryStatus === COMPLETE ||
      (searchType === '3'
        ? !formik.values.fullName.trim()
        : (searchType === '1' && !formik.values.memberNumber) ||
          (searchType === '2' && !formik.values.documentNumber));

    return (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={4}
        w="full"
        align="center"
      >
        <Button
          type="submit"
          colorScheme="green"
          size="lg"
          isDisabled={isSearchDisabled}
          isLoading={queryStatus === IN_PROGRESS}
          loadingText="Buscando..."
          leftIcon={<FiSearch />}
          flex={1}
          _hover={{
            transform: 'translateY(-2px)',
            boxShadow: 'lg'
          }}
        >
          Buscar socio
        </Button>
        <Button
          type="button"
          variant="outline"
          colorScheme="green"
          size="lg"
          isDisabled={queryStatus !== COMPLETE}
          onClick={retrySearch}
          leftIcon={<FiRefreshCw />}
          flex={1}
          _hover={{
            transform: 'translateY(-2px)',
            boxShadow: 'lg'
          }}
        >
          Nueva búsqueda
        </Button>
      </Stack>
    );
  };

  return (
    <Box minH="100vh" bgGradient={bgGradient} py={8} className="fade-in-up">
      <Container maxW="4xl" centerContent>
        <Flex direction="column" align="center" w="full" mb={8}>
          <HStack justify="space-between" w="full" mb={4}>
            <Box />
            <ColorModeToggle />
          </HStack>

          <Card
            w="full"
            maxW="2xl"
            shadow="2xl"
            bg={cardBg}
            borderRadius="2xl"
            border="1px"
            borderColor={useColorModeValue('green.100', 'gray.700')}
            className="card-hover"
            overflow="hidden"
          >
            <CardHeader
              bg={headingColor}
              color="white"
              textAlign="center"
              py={6}
            >
              <VStack spacing={2}>
                <Icon as={FiUserCheck} boxSize={8} />
                <Heading size="lg" fontWeight="bold">
                  ¿Ya sos socio?
                </Heading>
                <Text fontSize="sm" opacity={0.9}>
                  Vinculá tu cuenta con tu número de socio
                </Text>
              </VStack>
            </CardHeader>

            <CardBody p={8}>
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={8}>
                  <VStack spacing={4} align="stretch">
                    <Text color={textColor} textAlign="left" fontSize="md">
                      Para vincular tu cuenta, necesitamos tu número de socio, número de documento de identidad o nombre y/o apellido. Por favor, seleccioná la opción por la cual querés buscar.
                    </Text>

                    <Box display="flex" justifyContent="center" alignItems="center" w="full">
                      <RadioButtons
                        value={searchType}
                        callback={handleRadioButtonChange}
                        isDisabled={queryStatus === COMPLETE}
                      />
                    </Box>
                  </VStack>

                  <VStack spacing={6}>
                    {searchType === '3' && (
                      <FormControl
                        isInvalid={
                          (formik.touched.fullName ?? false) &&
                          formik.errors.fullName != null
                        }
                      >
                        <FormLabel
                          htmlFor="full-name"
                          color={textColor}
                          fontWeight="semibold"
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                          <Icon as={FiUser} />
                          Nombre y/o apellido
                        </FormLabel>
                        <Input
                          id="full-name"
                          name="fullName"
                          type="text"
                          value={formik.values.fullName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isDisabled={queryStatus === COMPLETE}
                          placeholder="Ingresá tu nombre y/o apellido"
                          size="lg"
                          borderRadius="lg"
                          focusBorderColor="green.500"
                          _hover={{
                            borderColor: 'green.300'
                          }}
                        />
                        <FormErrorMessage>
                          {formik.errors.fullName}
                        </FormErrorMessage>
                      </FormControl>
                    )}

                    {searchType === '1' && (
                      <FormControl
                        isInvalid={
                          (formik.touched.memberNumber ?? false) &&
                          formik.errors.memberNumber != null
                        }
                      >
                        <FormLabel
                          htmlFor="member-number"
                          color={textColor}
                          fontWeight="semibold"
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                          <Icon as={FiHash} />
                          Número de socio
                        </FormLabel>
                        <Input
                          id="member-number"
                          name="memberNumber"
                          type="number"
                          value={formik.values.memberNumber}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isDisabled={queryStatus === COMPLETE}
                          placeholder="Ingresá tu número de socio"
                          size="lg"
                          borderRadius="lg"
                          focusBorderColor="green.500"
                          _hover={{
                            borderColor: 'green.300'
                          }}
                        />
                        <FormErrorMessage>
                          {formik.errors.memberNumber}
                        </FormErrorMessage>
                      </FormControl>
                    )}

                    {searchType === '2' && (
                      <FormControl
                        isInvalid={
                          (formik.touched.documentNumber ?? false) &&
                          formik.errors.documentNumber != null
                        }
                      >
                        <FormLabel
                          htmlFor="document-number"
                          color={textColor}
                          fontWeight="semibold"
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                          <Icon as={FiCreditCard} />
                          Número de documento
                        </FormLabel>
                        <Input
                          id="document-number"
                          name="documentNumber"
                          type="number"
                          value={formik.values.documentNumber}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isDisabled={queryStatus === COMPLETE}
                          placeholder="Ingresá tu número de documento"
                          size="lg"
                          borderRadius="lg"
                          focusBorderColor="green.500"
                          _hover={{
                            borderColor: 'green.300'
                          }}
                        />
                        <FormErrorMessage>
                          {formik.errors.documentNumber}
                        </FormErrorMessage>
                      </FormControl>
                    )}

                    {queryStatus === COMPLETE && SEARCH_WARNING_MESSAGES[queryResult] && (
                      <Alert status="warning" borderRadius="lg">
                        <AlertIcon />
                        <AlertDescription>
                          {SEARCH_WARNING_MESSAGES[queryResult]}
                        </AlertDescription>
                      </Alert>
                    )}

                    {queryStatus === IN_PROGRESS && (
                      <Alert status="info" borderRadius="lg">
                        <Spinner size="sm" mr={2} />
                        <AlertDescription>
                          Buscando información del socio...
                        </AlertDescription>
                      </Alert>
                    )}
                  </VStack>

                  <SearchButtons />
                </Stack>
              </form>
            </CardBody>
          </Card>

          {queryStatus === COMPLETE && queryResult === FOUND && partnerData && (
            <Card
              w="full"
              maxW="2xl"
              mt={8}
              shadow="2xl"
              bg={cardBg}
              borderRadius="2xl"
              border="1px"
              borderColor={successCardBorder}
              className="card-hover"
            >
              <CardHeader
                bg={successCardHeaderBg}
                color="white"
                textAlign="center"
                py={4}
              >
                <HStack justify="center" spacing={2}>
                  <Icon as={FiUserCheck} boxSize={6} />
                  <Heading size="md" fontWeight="bold">
                    ¡Socio encontrado!
                  </Heading>
                </HStack>
              </CardHeader>
              <CardBody p={8}>
                <PartnerDataBox member={partnerData} />
              </CardBody>
            </Card>
          )}
        </Flex>
      </Container>
    </Box>
  );
}

export default FirstLogin;
